import React from "react";
import clsx from "clsx";
import LandingGlyph from "./LandingGlyph";
import StarRating from "./StarRating";
import { averageTestimonialRating } from "./landingTestimonialUtils";

/**
 * Shape intentionally mirrors the fields a review API response already carries
 * (rating, message, reviewer name, gig title, gig city/state) so this section can
 * be swapped from the hardcoded landing list to live featured reviews without a
 * component change.
 */
export type LandingTestimonial = {
  id: string;
  rating: number;
  message: string;
  authorName: string;
  authorRole: string;
  gigTitle: string;
  location: string;
};

const testimonialTones = [
  { avatar: "bg-[#0F766E]", chip: "border-[#14B8A6]/35 bg-[#ECFDF5] text-[#065F46]" },
  { avatar: "bg-[#C2410C]", chip: "border-[#F97316]/35 bg-[#FFF7ED] text-[#9A3412]" },
  { avatar: "bg-myteengig-primary", chip: "border-myteengig-primary/25 bg-[#EAF8FF] text-myteengig-text-primary" },
  { avatar: "bg-[#A16207]", chip: "border-[#EAB308]/40 bg-[#FEFCE8] text-[#854D0E]" },
];

const toInitials = (authorName: string) =>
  authorName
    .split(" ")
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export type LandingTestimonialsProps = React.ComponentPropsWithoutRef<"section"> & {
  testimonials: LandingTestimonial[];
  eyebrow: string;
  title: string;
  description: string;
  footnote?: string;
  /** Overrides the average computed from `testimonials` (e.g. the platform-wide average). */
  averageRating?: number;
  /** Overrides the summary caption under the average, which otherwise counts the featured list. */
  averageLabel?: string;
};

const LandingTestimonials: React.FC<LandingTestimonialsProps> = ({
  testimonials,
  eyebrow,
  title,
  description,
  footnote,
  averageRating,
  averageLabel,
  className,
  ...props
}) => {
  const average = averageRating ?? averageTestimonialRating(testimonials);
  const summaryLabel =
    averageLabel ?? `Average of ${testimonials.length} featured ${testimonials.length === 1 ? "review" : "reviews"}`;

  return (
    <section
      className={clsx("bg-[linear-gradient(180deg,#F6FCFF_0%,#FFFFFF_60%)] px-4 py-16 sm:px-6 lg:px-10", className)}
      {...props}
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
          <div>
            <p className="inline-flex items-center gap-2 rounded-md border border-myteengig-border bg-white px-3 py-1.5 text-xs font-bold tracking-wide text-myteengig-primary uppercase shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F97316]" />
              {eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-bold text-myteengig-text-primary">{title}</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-myteengig-text-muted">{description}</p>
          </div>

          <div className="flex items-center gap-4 rounded-2xl border border-myteengig-border bg-white p-5 shadow-[0_18px_50px_rgba(10,70,102,0.08)]">
            <p className="text-5xl leading-none font-black text-myteengig-text-primary">{average.toFixed(1)}</p>
            <div className="min-w-0">
              <StarRating rating={average} />
              <p className="mt-1 text-sm font-bold text-myteengig-text-primary">{summaryLabel}</p>
              <p className="mt-0.5 text-xs leading-5 text-myteengig-text-muted">
                Teens and gig posters rate each other after a gig wraps up.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {testimonials.map((testimonial, index) => {
            const tone = testimonialTones[index % testimonialTones.length];

            return (
              <article
                key={testimonial.id}
                className="flex h-full flex-col rounded-xl border border-myteengig-border bg-white p-5 shadow-xs transition duration-200 hover:-translate-y-1 hover:border-myteengig-primary/40 hover:shadow-[0_18px_40px_rgba(10,70,102,0.12)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <StarRating rating={testimonial.rating} />
                  <LandingGlyph name="chat" className="h-5 w-5 text-myteengig-primary/40" />
                </div>

                <blockquote className="mt-3 flex-1 text-sm leading-6 text-myteengig-text-primary">
                  {`"${testimonial.message}"`}
                </blockquote>

                <div className="mt-4 flex items-center gap-3 border-t border-myteengig-border/70 pt-4">
                  <span
                    className={clsx(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-black text-white",
                      tone.avatar,
                    )}
                    aria-hidden="true"
                  >
                    {toInitials(testimonial.authorName)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-myteengig-text-primary">{testimonial.authorName}</p>
                    <p className="truncate text-xs text-myteengig-text-muted">{testimonial.location}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={clsx("rounded-md border px-2 py-1 text-[11px] font-bold", tone.chip)}>
                    {testimonial.authorRole}
                  </span>
                  <span className="truncate rounded-md border border-myteengig-border bg-myteengig-background px-2 py-1 text-[11px] font-semibold text-myteengig-text-muted">
                    {testimonial.gigTitle}
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        {footnote ? <p className="mt-6 text-sm text-myteengig-text-muted">{footnote}</p> : null}
      </div>
    </section>
  );
};

export default LandingTestimonials;
